// component-user-picker.ts — Modale de sélection d'un utilisateur (reusable).
//
// Migration S4.1 du plan TS+Vite (2026-05-10).

interface PickableUser {
  id: number;
  trigram?: string | null;
  name: string;
  is_team?: boolean;
  is_external?: boolean;
}

interface ShowUserPickerOpts {
  title?: string;
  excludeIds?: ReadonlyArray<number>;
  // Conservé pour compat — non utilisé dans l'implémentation actuelle.
  // Le filtrage des équipes se fait via excludeIds par le caller.
  includeTeams?: boolean;
  // Si vrai, inclut les utilisateurs inactifs / dont la date de départ est
  // passée. Cas d'usage unique : page #user-replacement, picker
  // "Utilisateur à remplacer". Défaut false : on n'autorise qu'un user
  // actif dans les pickers métier.
  includeInactive?: boolean;
}

declare global {
  interface Window {
    showUserPicker: typeof showUserPicker;
  }
}

function showUserPicker(
  onSelected: (u: PickableUser) => void,
  opts: ShowUserPickerOpts = {},
): void {
  const title = opts.title || t('userPicker.defaultTitle');
  const excludeIds = opts.excludeIds || [];

  void loadUsers().then((users) => {
    // On garde les équipes par défaut. Si un caller veut les exclure,
    // il devra passer `opts.excludeIds` avec les IDs des équipes.
    // (Avant : `if (!opts.includeTeams) pool = pool;` — no-op détecté
    // par ESLint, supprimé. La logique d'exclusion des teams n'a jamais
    // été implémentée.)
    // Filtrage actifs par défaut (cf. user 2026-05-21) sauf override
    // explicite via includeInactive (page user-replacement).
    const pool: PickableUser[] = (users as PickableUser[])
      .filter((u) => !excludeIds.includes(u.id))
      .filter((u) => opts.includeInactive || isUserActive(u as never));

    let filtered: PickableUser[] = [...pool];

    const renderRows = (): string => {
      if (filtered.length === 0)
        return `<tr><td colspan="3" style="padding:12px;text-align:center;color:var(--text3);font-size:12px">${esc(t('userPicker.empty'))}</td></tr>`;
      return filtered
        .map(
          (u) => `<tr style="cursor:pointer" data-pick-user="${u.id}">
        <td style="font-size:12px;padding:6px 8px">
          ${trigramHtml(u, { extraStyle: 'background:var(--bg3);padding:1px 6px;border-radius:4px' })}
        </td>
        <td style="font-size:13px;padding:6px 8px">${esc(u.name)}</td>
        <td style="font-size:11px;color:var(--text3);padding:6px 8px">${esc(u.is_team ? t('userPicker.typeTeam') : u.is_external ? t('userPicker.typeExternal') : t('userPicker.typeInternal'))}</td>
      </tr>`,
        )
        .join('');
    };

    const body = `
      <div style="margin-bottom:10px">
        <input type="text" id="up-search" placeholder="${esc(t('userPicker.search'))}" autocomplete="off"
          style="width:100%;font-size:13px;padding:7px 10px;border:1px solid var(--border2);border-radius:var(--radius)">
      </div>
      <div style="max-height:320px;overflow-y:auto">
        <table style="width:100%;border-collapse:collapse">
          <thead><tr>
            <th style="font-size:11px;color:var(--text3);padding:4px 8px;border-bottom:1px solid var(--border)">${esc(t('userPicker.colTrigram'))}</th>
            <th style="font-size:11px;color:var(--text3);padding:4px 8px;border-bottom:1px solid var(--border)">${esc(t('userPicker.colName'))}</th>
            <th style="font-size:11px;color:var(--text3);padding:4px 8px;border-bottom:1px solid var(--border)">${esc(t('userPicker.colType'))}</th>
          </tr></thead>
          <tbody id="up-tbody">${renderRows()}</tbody>
        </table>
      </div>
      <div style="margin-top:8px;font-size:11px;color:var(--text3)">${esc(t('userPicker.help'))}</div>`;

    openModal(title, body, () => closeModal(), t('userPicker.btnClose'));
    setTimeout(() => {
      const confirmBtn = document.getElementById('modal-confirm');
      if (confirmBtn) confirmBtn.textContent = t('userPicker.btnClose');
      const cancelBtn = document.getElementById('modal-cancel');
      if (cancelBtn) cancelBtn.style.display = 'none';

      document.getElementById('up-search')?.addEventListener('input', (e: Event) => {
        const target = e.target as HTMLInputElement | null;
        const q = (target?.value ?? '').toLowerCase().trim();
        filtered = q
          ? pool.filter(
              (u) =>
                u.name.toLowerCase().includes(q) || (u.trigram || '').toLowerCase().includes(q),
            )
          : [...pool];
        const tbody = document.getElementById('up-tbody');
        if (tbody) tbody.innerHTML = renderRows();
        wirePickRows();
      });

      wirePickRows();
    }, 0);

    function wirePickRows(): void {
      document.querySelectorAll<HTMLTableRowElement>('[data-pick-user]').forEach((row) => {
        row.addEventListener('click', () => {
          const uid = parseInt(row.dataset['pickUser'] || '');
          const u = pool.find((x) => x.id === uid);
          if (u) {
            closeModal();
            onSelected(u);
          }
        });
        row.addEventListener('mouseover', () => {
          row.style.background = 'var(--bg2)';
        });
        row.addEventListener('mouseout', () => {
          row.style.background = '';
        });
      });
    }
  });
}

// ── ESM globals shim (lot B.8 + S4.1) ──
window.showUserPicker = showUserPicker;

export {};
